import React from 'react';

import useIsMinBreakpoint from '@hero/react-hooks/mediaQuery/useIsMinBreakpoint';
import { useFormContext } from '@hero/ui-kit/inputs/Form';
import InputError from '@hero/ui-kit/inputs/InputError';
import Container from '@hero/ui-kit/layout/Container';
import Section from '@hero/ui-kit/layout/Section';
import P from '@hero/ui-kit/typography/P';
import useGetLeadInsurance from '../../../../../api/useGetLeadInsurance';
import ExistingHeroUserFormFields from '../../../../../components/ExistingHeroUserFormFields';
import RadioButton from '../../../../../components/RadioButton';
import VerifiedUser from '../../../../../components/VerifiedUser';
import { LeadDetailsResponse, LeadInsuranceResponse } from '../../../../../types';

interface Props {
    isLoading?: boolean;
    errorType?: string;
    lead?: LeadDetailsResponse;
    canEdit?: boolean;
    allowVerifiedLock?: boolean;
}

const FormElements: React.FC<{
    isSaveDisabled?: boolean;
    showAdminEmailError?: boolean;
    showHeroSerialErrorError?: boolean;
    leadInsurance?: LeadInsuranceResponse;
    lead?: LeadDetailsResponse;
    canEdit?: boolean;
    allowVerifiedLock?: boolean;
}> = ({
    showAdminEmailError = false,
    showHeroSerialErrorError = false,
    lead,
    canEdit,
    allowVerifiedLock = true
}) => {
    const isMinL = useIsMinBreakpoint('l');
    const {
        setValue,
        clearErrors,
        watch,
        formState: { isDirty, errors }
    } = useFormContext();

    const isMember = watch('is_member');

    React.useEffect(() => {
        setValue('isDirty', isDirty);
    }, [setValue, isDirty]);

    const isElectionMember = ['backoffice', 'mobile'].includes(lead?.form_version || '');

    const setIsMember = React.useCallback(
        (value: boolean) => {
            setValue('is_member', value);
            clearErrors('is_member');
        },
        [setValue]
    );

    return (
        <>
            <Section noDefaultPadding>
                <div style={{ width: '100%', textAlign: 'left', marginBottom: 'var(--gutter)' }}>
                    <P
                        noDefaultMargin
                        strong
                        styles={{ fontSize: '2.4rem', marginBottom: '3.6rem' }}
                    >
                        Existing member
                    </P>
                </div>

                {allowVerifiedLock &&
                lead?.primary_user_details.external_serial &&
                lead.primary_user_details.admin_email &&
                lead.is_existing_member &&
                isElectionMember ? (
                    <VerifiedUser
                        adminEmail={lead.primary_user_details.admin_email}
                        serialNumber={lead.primary_user_details.external_serial}
                    />
                ) : (
                    <>
                        <Container
                            gridTemplateColumns={isMinL ? '1fr 1fr' : '1fr'}
                            gridColumnGap="regular"
                        >
                            <RadioButton
                                checked={isMember === true}
                                onClick={() => setIsMember(true)}
                                title="Existing Hero user"
                                subtitle="Applicant already has Hero device."
                                error={!!errors?.is_member}
                            />
                            <RadioButton
                                checked={isMember === false}
                                onClick={() => setIsMember(false)}
                                title="Order new Hero device"
                                subtitle="New Hero device will be shipped."
                                error={!!errors?.is_member}
                            />
                        </Container>

                        {errors.is_member && (
                            <InputError id={'is_member'}>
                                {errors.is_member.message as string}
                            </InputError>
                        )}

                        {isMember ? (
                            <Section noDefaultPadding marginVertical="regular">
                                <ExistingHeroUserFormFields
                                    canEdit={canEdit}
                                    showAdminEmailError={showAdminEmailError}
                                    showHeroSerialErrorError={showHeroSerialErrorError}
                                />
                            </Section>
                        ) : null}
                    </>
                )}
            </Section>
        </>
    );
};

const ExistingMemberForm: React.FC<Props> = ({
    errorType,
    isLoading,
    lead,
    canEdit,
    allowVerifiedLock
}) => {
    const { data: leadInsurance } = useGetLeadInsurance(lead?.primary_user_details.lead_id || '');

    return (
        <>
            <FormElements
                leadInsurance={leadInsurance}
                isSaveDisabled={isLoading}
                showAdminEmailError={errorType === 'email_not_exist'}
                showHeroSerialErrorError={errorType === 'serial_not_exist'}
                lead={lead}
                canEdit={canEdit}
                allowVerifiedLock={allowVerifiedLock}
            />
        </>
    );
};

export default ExistingMemberForm;
